import {
  FiltersDimensionDataQuadrant,
  FiltersMeasuresDataQuadrant,
} from "../../components/Filter/FilterData/quadrantFilter";

const SPEND_STAT_KEYS = {
  amazon: ["cost", "spends"],
  flipkart: ["spends", "cost"],
  myntra: ["spends", "cost"],
};

const snakeToCamel = (value = "") =>
  value.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

const camelToSnake = (value = "") =>
  value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const buildStatKeyVariants = (key) => {
  if (!key) return [];

  const variants = new Set([key, snakeToCamel(key), camelToSnake(key)]);

  if (key.includes(".")) {
    const lastSegment = key.split(".").pop();
    variants.add(lastSegment);
    variants.add(snakeToCamel(lastSegment));
    variants.add(camelToSnake(lastSegment));
  }

  return [...variants].filter(Boolean);
};

export const getValueFromProductStats = (productStats, ...keys) => {
  if (!productStats) return null;

  const candidateKeys = [
    ...new Set(keys.flatMap((key) => buildStatKeyVariants(key))),
  ];

  for (const key of candidateKeys) {
    const value = productStats[key];
    if (value != null && value !== "") {
      return value;
    }
  }

  return null;
};

const resolveStatsKey = (field) => {
  if (field?.apiKey) {
    const key = field?.id;
    if (key) return key.split(".").pop();
  }
  const id = field?.id ?? "";
  if (id.includes(".")) return id.split(".").pop();
  return id;
};

const addFieldAliases = (map, key, config) => {
  if (!key) return;

  buildStatKeyVariants(key).forEach((alias) => {
    map[alias] = config;
  });
};

const buildFieldMap = (fields = []) =>
  fields.reduce((map, field) => {
    const config = {
      name: field.name,
      statsKey: resolveStatsKey(field),
      grouping: field.grouping,
      fieldType: field.datatype,
    };

    addFieldAliases(map, field.id, config);
    return map;
  }, {});

export const getPlatformFieldMap = (platform) => {
  const measureFields = FiltersMeasuresDataQuadrant[platform]?.fields ?? [];
  const dimensionFields = FiltersDimensionDataQuadrant[platform]?.fields ?? [];
  return {
    ...buildFieldMap(measureFields),
    ...buildFieldMap(dimensionFields),
  };
};

export const resolveFieldConfig = (platformFieldMap, member) => {
  if (!member || !platformFieldMap) return null;

  for (const key of buildStatKeyVariants(member)) {
    if (platformFieldMap[key]) {
      return platformFieldMap[key];
    }
  }

  return null;
};

/** @deprecated Use getPlatformFieldMap */
export const getPlatformMeasureFieldMap = getPlatformFieldMap;

const collectFilterMembers = (filter, members = []) => {
  if (!filter) return members;

  if (filter.type === "RULE_ITEM") {
    if (filter.member && !filter.member.includes("profileId")) {
      members.push(filter.member);
    }
    return members;
  }

  const conditions = filter.and || filter.or || [];
  conditions.forEach((condition) => collectFilterMembers(condition, members));
  return members;
};

export const getConfiguredFilterMembers = (filters) => {
  const members = [];

  if (filters?.measures) {
    members.push(...collectFilterMembers(filters.measures));
  }
  if (filters?.dimensions) {
    members.push(...collectFilterMembers(filters.dimensions));
  }

  return [...new Set(members)];
};

/** @deprecated Use getConfiguredFilterMembers */
export const getConfiguredMeasureMembers = getConfiguredFilterMembers;

export const formatChildMetricValue = (value) => {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  }
  return String(value);
};

export const getMetricValueFromProductStats = (
  productStats,
  fieldConfig,
  platform
) => {
  if (!productStats || !fieldConfig) return null;

  const isSpendMetric =
    fieldConfig.statsKey === "cost" ||
    fieldConfig.statsKey === "spends" ||
    fieldConfig.grouping === "Spends" ||
    fieldConfig.name?.toLowerCase() === "spends";

  const candidateKeys = [
    fieldConfig.statsKey,
    ...(isSpendMetric ? SPEND_STAT_KEYS[platform] ?? [] : []),
  ].filter(Boolean);

  return getValueFromProductStats(productStats, ...candidateKeys);
};
